import { getTOtp, syncConnections } from "@/api";
import Loading from "@/components/Loading";
import { useSyncContext } from "@/components/SyncProvider";
import { AuthSignUpPageContainer } from "@/components/pages/Auth/AuthSignUpPageOutlet";
import AuthSolidButton from "@/components/pages/Auth/components/AuthSolidButton";
import { useAppSelector } from "@/redux/hooks";
import { Retailer } from "@/redux/types";
import { RetailerSyncFlowTypes } from "@brandclub/types";
import * as CryptoJS from "crypto-js";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckoutInputV2 } from "../../../CheckoutInputV2";
import { AccountConnectBenefits } from "../../../CheckoutSummary/components/AccountRewards";

const ROW_SPACING = 25;

export const CheckoutConnectRetailer = ({
  retailerId: retailerIdFromProps,
  onConnectCallback,
  connectOverwrite,
  redirectToSync = true,
  mode = RetailerSyncFlowTypes.CONNECT,
  errorMsg,
  className,
  additionalRewardAmount,
  disabled = false,
  buttonText = "Connect",
}: {
  retailerId?: number;
  onConnectCallback?: (retailerId: number) => Promise<void>;
  connectOverwrite?: (userName: string, password: string) => void;
  redirectToSync?: boolean;
  mode?: RetailerSyncFlowTypes.FIX | RetailerSyncFlowTypes.CONNECT;
  errorMsg?: string;
  className?: string;
  additionalRewardAmount?: number;
  disabled?: boolean;
  buttonText?: string;
}) => {
  const [username, setUsername] = useState<string>();
  const [password, setPassword] = useState<string>();
  const [retailer, setRetailer] = useState<Retailer>();
  const { retailerId: retailerIdFromParams } = useParams();
  const retailers = useAppSelector(({ retailers }) => retailers);
  const { connectRetailer } = useSyncContext();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const getUserNameFieldHeading = (retailer: Retailer) => {
    if (retailer.retailerLoginUsernameFieldTypes?.length) {
      const supportedFieldTypes = retailer.retailerLoginUsernameFieldTypes;
      if (
        supportedFieldTypes.includes("email") &&
        supportedFieldTypes.includes("phoneNumber")
      ) {
        return "Email or mobile phone";
      }
    }
    return "Email";
  };

  useEffect(() => {
    if (retailers && (retailerIdFromParams || retailerIdFromProps)) {
      const currentRetailer = retailers.find(
        (r) =>
          r.retailerId ===
          parseInt(`${retailerIdFromParams || retailerIdFromProps}`)
      );
      if (currentRetailer) {
        setRetailer(currentRetailer);
      }
    }
  }, [retailerIdFromParams, retailerIdFromProps, retailers]);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        if (retailer) {
          const { token, currentStamp } = await getTOtp();
          const accountsDataEncrypted = await syncConnections({ currentStamp });
          const accounts = JSON.parse(
            CryptoJS.AES.decrypt(accountsDataEncrypted, token).toString(
              CryptoJS.enc.Utf8
            )
          );
          const account = accounts?.find(
            (x: any) => x.retailerId === retailer.parentRetailerId
          );
          if (account) {
            setUsername(account.credential.username);
            setPassword(account.credential.password);
          }
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [retailer]);

  return (
    <AuthSignUpPageContainer className={className} sx={{}}>
      {!retailer || loading ? (
        <div className="page_title">
          <Loading dot />
        </div>
      ) : (
        <>
          <CheckoutInputV2
            label={getUserNameFieldHeading(retailer)}
            inputStyle={{ width: "100%" }}
            style={{ marginTop: ROW_SPACING }}
            onChange={(e) => {
              setUsername(e.target.value as any);
            }}
            sx={{ maxWidth: "100%" }}
            disabled={submitting || disabled}
            value={username}
          />
          <CheckoutInputV2
            label="Password"
            type="password"
            inputStyle={{ width: "100%" }}
            style={{ marginTop: ROW_SPACING }}
            onChange={(e) => {
              setPassword(e.target.value as any);
            }}
            sx={{ maxWidth: "100%" }}
            disabled={submitting || disabled}
            value={password}
            errorMsg={errorMsg}
          />
          <AuthSolidButton
            style={{
              marginTop: ROW_SPACING + 5,
            }}
            disabled={
              submitting ||
              disabled ||
              !username ||
              !password ||
              username.length < 1 ||
              password.length < 1
            }
            onClick={async () => {
              setSubmitting(true);
              if (username && password && connectOverwrite) {
                connectOverwrite(username, password);
              } else if (username && password) {
                connectRetailer(
                  retailer.parentRetailerId || retailer.retailerId,
                  username,
                  password,
                  mode,
                  redirectToSync
                );
                if (onConnectCallback) {
                  await onConnectCallback(
                    retailer.parentRetailerId || retailer.retailerId
                  );
                }
              }
              setSubmitting(false);
            }}
          >
            {buttonText}
          </AuthSolidButton>
          <div style={{ marginTop: ROW_SPACING, width: "100%" }}>
            <AccountConnectBenefits retailerId={retailer.retailerId} />
          </div>
        </>
      )}
    </AuthSignUpPageContainer>
  );
};
